import { useState } from 'react';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { Async } from '../components/Async';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import {
  getPayees,
  getPharmacyCosts,
  createPharmacyCost,
  updatePharmacyCost,
  deletePharmacyCost,
  ApiError,
  type PharmacyCostRow,
} from '../lib/api';
import { fmtCents } from '../lib/format';

export const PharmacyCostsPage = () => {
  const { can } = useAuth();
  const canManage = can('pricing:manage');
  const payees = useFetch(getPayees);
  const costs = useFetch(getPharmacyCosts);
  const [msg, setMsg] = useState<string | null>(null);

  const nameFor = (ncpdpid: string) =>
    payees.data?.payees.find((p) => p.ncpdpid === ncpdpid)?.name ?? ncpdpid;

  async function save(fn: () => Promise<unknown>, ok: string) {
    setMsg(null);
    try {
      await fn();
      setMsg(ok);
      costs.reload();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Save failed');
    }
  }

  return (
    <Page>
      <PageHeader
        title="Pharmacy Costs"
        sub="What each 3rd-party pharmacy charges us per fill — the cost table behind pharmacy payables. Seed/sync from core with pnpm --filter server seed:pharmacy-costs."
      />
      {msg && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(59,130,246,.08)', color: 'var(--status-in-progress-ink,#1d4ed8)', fontSize: 13 }}>
          {msg}
        </div>
      )}

      <Async state={costs} isEmpty={(d) => d.costs.length === 0} emptyLabel="No costs yet — run the seed or add one below.">
        {({ costs: rows }) => {
          const byPayee = new Map<string, PharmacyCostRow[]>();
          for (const r of rows) {
            const list = byPayee.get(r.ncpdpid) ?? [];
            list.push(r);
            byPayee.set(r.ncpdpid, list);
          }
          return (
            <>
              {[...byPayee.entries()].map(([ncpdpid, list]) => (
                <div key={ncpdpid}>
                  <div className="section-title">{nameFor(ncpdpid)} · NCPDP {ncpdpid}</div>
                  <PCard>
                    <table className="p-table">
                      <thead>
                        <tr>
                          <th>Drug</th>
                          <th style={{ textAlign: 'right' }}>Qty</th>
                          <th>Unit</th>
                          <th style={{ textAlign: 'right' }}>Cost / fill</th>
                          {canManage && <th style={{ textAlign: 'right' }}>Edit</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((c) => (
                          <CostRow key={c.id} cost={c} canManage={canManage} onSave={save} />
                        ))}
                      </tbody>
                    </table>
                  </PCard>
                </div>
              ))}
              {canManage && (
                <AddCostForm
                  payees={payees.data?.payees ?? []}
                  onAdd={(body) => save(() => createPharmacyCost(body), `Added ${body.drugName}.`)}
                />
              )}
            </>
          );
        }}
      </Async>
    </Page>
  );
};

const CostRow = ({
  cost,
  canManage,
  onSave,
}: {
  cost: PharmacyCostRow;
  canManage: boolean;
  onSave: (fn: () => Promise<unknown>, ok: string) => Promise<void>;
}) => {
  const [editing, setEditing] = useState(false);
  const [dollars, setDollars] = useState((cost.costCents / 100).toFixed(2));

  return (
    <tr>
      <td>{cost.drugName}{cost.label && <span className="muted"> · {cost.label}</span>}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>{cost.dispenseQuantity}</td>
      <td className="muted">{cost.dispenseUnit}</td>
      <td className="tabular" style={{ textAlign: 'right' }}>
        {editing ? (
          <input
            style={{ width: 80, textAlign: 'right' }}
            value={dollars}
            onChange={(e) => setDollars(e.target.value)}
            autoFocus
          />
        ) : (
          fmtCents(cost.costCents)
        )}
      </td>
      {canManage && (
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          {editing ? (
            <>
              <button
                className="btn"
                onClick={() =>
                  onSave(() => updatePharmacyCost(cost.id, Math.round(parseFloat(dollars) * 100)), 'Updated.').then(() =>
                    setEditing(false),
                  )
                }
              >
                Save
              </button>{' '}
              <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
            </>
          ) : (
            <>
              <button className="btn" onClick={() => setEditing(true)}>Edit</button>{' '}
              <button className="btn" onClick={() => onSave(() => deletePharmacyCost(cost.id), 'Deleted.')}>Delete</button>
            </>
          )}
        </td>
      )}
    </tr>
  );
};

const AddCostForm = ({
  payees,
  onAdd,
}: {
  payees: { ncpdpid: string; name: string }[];
  onAdd: (body: { ncpdpid: string; drugName: string; dispenseQuantity: number; dispenseUnit: string; costCents: number }) => void;
}) => {
  const [ncpdpid, setNcpdpid] = useState(payees[0]?.ncpdpid ?? '');
  const [drugName, setDrugName] = useState('');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('Milliliter');
  const [dollars, setDollars] = useState('');

  const valid = ncpdpid && drugName && qty && unit && dollars;

  return (
    <PCard style={{ padding: 16 }}>
      <div className="section-title" style={{ marginTop: 0 }}>Add a cost</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={ncpdpid} onChange={(e) => setNcpdpid(e.target.value)}>
          {payees.map((p) => <option key={p.ncpdpid} value={p.ncpdpid}>{p.name}</option>)}
        </select>
        <input placeholder="Drug name (e.g. Tirzepatide - 5mg/0.5ml)" value={drugName} onChange={(e) => setDrugName(e.target.value)} style={{ minWidth: 240 }} />
        <input placeholder="Qty" value={qty} onChange={(e) => setQty(e.target.value)} style={{ width: 70 }} />
        <input placeholder="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} style={{ width: 110 }} />
        <input placeholder="$ / fill" value={dollars} onChange={(e) => setDollars(e.target.value)} style={{ width: 90 }} />
        <button
          className="btn btn--primary"
          disabled={!valid}
          onClick={() => {
            onAdd({ ncpdpid, drugName, dispenseQuantity: parseFloat(qty), dispenseUnit: unit, costCents: Math.round(parseFloat(dollars) * 100) });
            setDrugName(''); setQty(''); setDollars('');
          }}
        >
          Add
        </button>
      </div>
    </PCard>
  );
};
